import { Injectable, signal } from '@angular/core';

/**
 * `priority`: likely mobile LCP — eager + preload. `eager`: near the fold — SSR'd, native lazy.
 * `deferred`: no `<img>` in SSR HTML, rendered on viewport so its bytes stay out of the LCP window.
 */
export type TeaserAssetLoading = 'priority' | 'eager' | 'deferred';

/** Provided by storyblok-root; teasers at any nesting depth read their mode by uid. */
@Injectable()
export class TeaserAssetLoadingStore {
  readonly modes = signal<ReadonlyMap<string, TeaserAssetLoading>>(new Map());
}

type Blok = { _uid?: string; component?: string; [key: string]: unknown };

/** Bloks in document order, descending into the layout containers that host teasers. */
function flattenBody(bloks: unknown[]): Blok[] {
  const out: Blok[] = [];
  for (const blok of bloks as Blok[]) {
    if (!blok?.component) continue;
    out.push(blok);
    if (blok.component === 'content_group' && Array.isArray(blok['blocks'])) out.push(...flattenBody(blok['blocks']));
    if (blok.component === 'two_column') {
      if (Array.isArray(blok['left'])) out.push(...flattenBody(blok['left']));
      if (Array.isArray(blok['right'])) out.push(...flattenBody(blok['right']));
    }
  }
  return out;
}

/**
 * `priority`: the first image teaser when nothing above it (hero, carousel, image) can be the LCP.
 * `eager`: within the first three bloks, or the first six when there is no hero above.
 * Everything further down: `deferred`.
 */
export function computeTeaserAssetLoading(body: unknown): Map<string, TeaserAssetLoading> {
  const map = new Map<string, TeaserAssetLoading>();
  if (!Array.isArray(body)) return map;

  let lcpTaken = false;
  flattenBody(body).forEach((blok, index) => {
    const component = blok.component as string;
    if (component === 'carousel' || component === 'image' || component.startsWith('hero')) {
      lcpTaken = true;
      return;
    }
    if (component !== 'teaser' || !blok._uid) return;

    const rawAsset = blok['asset'];
    const asset = (Array.isArray(rawAsset) ? rawAsset[0] : rawAsset) as { image?: { filename?: string } } | undefined;
    const nearTop = index < 3 || (!lcpTaken && index < 6);

    let mode: TeaserAssetLoading = 'deferred';
    if (nearTop) mode = 'eager';
    if (!lcpTaken && index < 6 && asset?.image?.filename && !blok['hideAssetOnMobile']) {
      mode = 'priority';
      lcpTaken = true;
    }
    map.set(blok._uid, mode);
  });
  return map;
}
